import React, { useContext, useState, useEffect } from 'react'
import { Row, Col, Spinner } from 'react-bootstrap'
import { Link, Redirect } from 'react-router-dom'
import { AuthContext, AuthState, UserType } from '../../contexts/authContext'
import { MigrantContext } from '../../contexts/migrantContext'
import { Consumer } from '../../models/Consumer'
import Layout from '../common/layout/layout'
import styles from './index.module.css'

const MigrantMain: React.FC = () => {
  const authContext = useContext(AuthContext)
  const migrantContext = useContext(MigrantContext)
  const [showLoading, setShowLoading] = useState(false)
  const isLoggedIn = authContext?.authState === AuthState.signedIn

  useEffect(() => {
    if (isLoggedIn && !migrantContext?.profile) {
      setShowLoading(true)

      // Dismiss modal loader after 15 seconds to prevent "unresponsive" app
      setTimeout(() => {
        setShowLoading(false)
      }, 15000)
    } else {
      setShowLoading(false)
    }
  }, [isLoggedIn, migrantContext])

  if (authContext?.userType !== UserType.migrant) {
    if (!isLoggedIn) {
      authContext?.setUserType(UserType.migrant)
    } else {
      // Redirect vendor to vendor dashboard
      return <Redirect to="/vendor" />
    }
  }

  return (
    <Layout>
      {showLoading && (
        <div className="modal-loader">
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading your account...</p>
        </div>
      )}

      <Row>
        <Col xs="12" className="px-0">
          <Banner />
        </Col>
      </Row>

      <Row className={styles.section}>
        <Col xs="12">
          {isLoggedIn && authContext?.userType === UserType.migrant ? (
            <WelcomeUser profile={migrantContext?.profile as Consumer} />
          ) : (
            <WelcomeAnonymous />
          )}
        </Col>
      </Row>

      {/* <Row className={styles.section}>
        <Col>
          <h1>Promos</h1>
          <Row className={`${styles.promos} text-center`}>
            <Col xs={6} lg={2} className={styles.promoItem}>
              <a href="https://bigmochi.com/shop/products/0sZrk0Qpk8Z1ApqnQeVo">
                <img src="promos/promo-oppo-r17.jpg" alt="Oppo R17" />
              </a>
            </Col>
            <Col xs={6} lg={2} className={styles.promoItem}>
              <a href="https://bigmochi.com/shop/products/nc6Kb7nlFn5RWhYqKR1P">
                <img src="promos/promo-oppo-ax7.jpg" alt="Oppo Ax7" />
              </a>
            </Col>
            <Col xs={6} lg={2} className={styles.promoItem}>
              <a href="https://bigmochi.com/shop/products/ZZZwMdUW86DXaLpAQtbx">
                <img src="promos/promo-vivo-y30.jpg" alt="Vivo Y30" />
              </a>
            </Col>
            <Col xs={6} lg={2} className={styles.promoItem}>
              <a href="https://bigmochi.com/shop/products/hmDdPRjE9JVnRnAbAG8R">
                <img
                  src="promos/promo-samsung -tab-a10.jpg"
                  alt="Samsung Tab A10"
                />
              </a>
            </Col>
            <Col xs={6} lg={2} className={styles.promoItem}>
              <a href="https://bigmochi.com/shop/products/SpuwuVT1hzYqFNddmU56">
                <img src="promos/promo-oppo-ax5.jpg" alt="Oppo Ax5" />
              </a>
            </Col>
          </Row>
        </Col>
      </Row> */}

      <Row className={styles.section}>
        <Col>
          <MenuIcons isLoggedIn={isLoggedIn} />
        </Col>
      </Row>
    </Layout>
  )
}

const WelcomeAnonymous: React.FC = () => {
  return (
    <>
      <Row data-testid="signup-prompt" className={styles.statusBlock}>
        <Col>
          <Row className={styles.welcomeText}>
            <Col>
              <span>Sign up to join BigMochi.</span>
            </Col>
          </Row>
          <Row className="pt-1">
            <Col>
              <Link to="/login">
                <button className="mochi-button white mr-3">Sign In</button>
              </Link>
              {/* <Link to="/login?action=signup">
                <button className="mochi-button orange"> Sign Up</button>
              </Link> */}
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  )
}

interface WelcomeUserProps {
  profile: Consumer
}

const WelcomeUser: React.FC<WelcomeUserProps> = ({ profile }) => {
  const authContext = useContext(AuthContext)
  const migrantContext = useContext(MigrantContext)
  const bal = migrantContext?.credits?.currentBalance ?? 0

  return (
    <>
      <Row>
        <Col>
          <h1>
            Hi{' '}
            {profile?.firstName
              ? profile?.firstName
              : authContext?.user?.displayName}
            !
          </h1>
        </Col>
      </Row>
      {profile && (
        <div>
          <img
            alt="user avatar"
            className={`avatar ${styles.floatingAvatar}`}
            src={migrantContext?.profile?.avatarUrl || '/profilepic.png'}
          />
        </div>
      )}
      <Row data-testid="userinfo-block" className={styles.statusBlock}>
        <Col>
          {profile ? (
            <span className={styles.credits}>
              <b data-testid="balance">{bal.toLocaleString()}</b> BMC
            </span>
          ) : (
            <span>Welcome to BigMochi!</span>
          )}
        </Col>
      </Row>
    </>
  )
}

const Banner: React.FC = () => {
  return (
    <div className={styles.banner}>
      <img src="banner.jpg" alt="Singapore city landscape" />
      {/* <div className={styles.bannerOverlay} />
      <span className={styles.bannerCaption}>
        We deliver straight to your house in the Philippines and Indonesia
      </span> */}
    </div>
  )
}

interface MenuIconsProps {
  isLoggedIn: boolean
}

const MenuIcons: React.FC<MenuIconsProps> = ({ isLoggedIn }) => {
  return (
    <Row className={styles.menu}>
      <Col
        xs="4"
        lg="3"
        className={styles.menuItem}
        data-testid="menu-item-shop"
      >
        <Link to="/shop">
          <div>
            <img src="icons/icon-cart.svg" alt="shop icon" />
          </div>
          <span>Shop</span>
        </Link>
      </Col>

      <Col
        xs="4"
        lg="3"
        className={styles.menuItem}
        data-testid="menu-item-jobs"
      >
        <Link to="/jobs">
          <div>
            <img src="icons/icon-jobs.svg" alt="jobs icon" />
          </div>
          <span>Jobs</span>
        </Link>
      </Col>

      {isLoggedIn && (
        <Col
          xs="4"
          lg="3"
          className={styles.menuItem}
          data-testid="menu-item-wallet"
        >
          <Link to="/wallet">
            <div>
              <img src="icons/icon-wallet.svg" alt="wallet icon" />
            </div>
            <span>Wallet</span>
          </Link>
        </Col>
      )}

      {/* <Col xs="4" lg="3" className={styles.menuItem}>
        <div>
          <img src="icons/icon-send.svg" alt="shop icon" />
        </div>
        <span>Send</span>
      </Col>
      <Col xs="4" lg="3" className={styles.menuItem}>
        <div>
          <img src="icons/icon-trending-up.svg" alt="shop icon" />
        </div>
        <span>Save</span>
      </Col>
      <Col xs="4" lg="3" className={styles.menuItem}>
        <div>
          <img src="icons/icon-borrow.svg" alt="shop icon" />
        </div>
        <span>Borrow</span>
      </Col>
      <Col xs="4" lg="3" className={styles.menuItem}>
        <div>
          <img src="icons/icon-bell.svg" alt="shop icon" />
        </div>
        <span>Notifications</span>
      </Col> */}
    </Row>
  )
}

export default MigrantMain
